import { useLayoutEffect, useRef, useState } from 'react';

/** How long cards that just came into the hand stay marked new. */
const NEW_FOR_MS = 3000;

/**
 * The cards that just came into your hand (drawn, a prize taken, a card returned), by their place in the hand
 * (pivot_id), for a few seconds. Not the hand you open the game with: only what arrives while you watch.
 *
 * @param {number[]} pivotIds the hand's cards now
 * @returns {Set<number>} the new ones
 */
export default function useNewInHand(pivotIds) {
  const seen = useRef(null);
  const [fresh, setFresh] = useState(() => new Set());
  const key = pivotIds.join(',');

  useLayoutEffect(() => {
    const before = seen.current;
    seen.current = new Set(pivotIds);
    if (!before) {
      return undefined;
    }
    const arrived = pivotIds.filter(id => !before.has(id));
    if (!arrived.length) {
      return undefined;
    }
    setFresh(current => new Set([...current, ...arrived]));
    const timer = setTimeout(() => setFresh(current => {
      const next = new Set(current);
      arrived.forEach(id => next.delete(id));
      return next;
    }), NEW_FOR_MS);
    return () => clearTimeout(timer);
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  return fresh;
}
